import React from 'react';
import ReactDOM from 'react-dom';
import { getAlgoliaResults } from '@algolia/autocomplete-js';
import {
  ADS_ADMIN_PREFIX,
  ALGOLIA_ALL_LISTINGS_INDEX,
  ALGOLIA_BREEDER_INDEX,
  ALGOLIA_LITTERS_INDEX,
  ALGOLIA_ORDERS_INDEX,
  ALGOLIA_SELLER_CONTACTS_INDEX,
  ALGOLIA_USERS_INDEX,
  LITTERS_ADMIN_PREFIX,
  ORDER_ADMIN_PREFIX,
  ORDER_ADMIN_VIEW_PREFIX,
  PROFILES_ADMIN_PREFIX,
  USER_ADMIN_PREFIX,
} from '../../../utils/constants';
import '@algolia/autocomplete-theme-classic';
import useCurrentUser from '../../../utils/hooks/useCurrentUser';
import { useAlgoliaSearchClientLite } from '../../../utils/hooks/useAlgoliaSearchClient';
import Autocomplete from './Autocomplete';
import SuggestedHit from './SuggestedHit';

const AdminGlobalSearch = () => {
  const { currentUser } = useCurrentUser();

  const { searchClient } = useAlgoliaSearchClientLite(currentUser?.public_key);

  const debouncePromise = (fn, time) => {
    let timerId;

    return function debounced(...args) {
      if (timerId) {
        clearTimeout(timerId);
      }

      return new Promise((resolve) => {
        timerId = setTimeout(() => resolve(fn(...args)), time);
      });
    };
  };

  const debounced = debouncePromise((items) => Promise.resolve(items), 500);

  return (
    <Autocomplete
      placeholder="Search Sellers, Ads, Contacts..."
      detachedMediaQuery="none"
      openOnFocus={false}
      getSources={({ query }) => {
        return debounced([
          {
            sourceId: 'suggestedPuppyAds',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: ALGOLIA_ALL_LISTINGS_INDEX!,
                    params: {
                      facetFilters: ['listings_type:puppy'],
                    },
                    query,
                  },
                ],
              });
            },
            templates: {
              header() {
                return <p className="global-search-result-header text-dark-gray">Puppy Ads</p>;
              },
              item({ item, components }) {
                return (
                  <SuggestedHit
                    hit={item}
                    components={components}
                    attribute="global_hit_title"
                    route={item.route}
                  />
                );
              },
              footer() {
                return (
                  <div className="mt-3">
                    <a
                      className="text-dark-gray"
                      href={`${ADS_ADMIN_PREFIX}?type=puppy&query=${query}`}
                    >
                      See all Puppy Ads
                    </a>
                  </div>
                );
              },
            },
          },
          {
            sourceId: 'suggestedStudAds',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: ALGOLIA_ALL_LISTINGS_INDEX!,
                    params: {
                      facetFilters: ['listings_type:stud'],
                    },
                    query,
                  },
                ],
              });
            },
            templates: {
              header() {
                return <p className="global-search-result-header text-dark-gray">Stud Ads</p>;
              },
              item({ item, components }) {
                return (
                  <SuggestedHit
                    hit={item}
                    components={components}
                    attribute="global_hit_title"
                    route={item.route}
                  />
                );
              },
              footer() {
                return (
                  <div className="mt-3">
                    <a
                      className="text-dark-gray"
                      href={`${ADS_ADMIN_PREFIX}?type=stud&query=${query}`}
                    >
                      See all Stud Ads
                    </a>
                  </div>
                );
              },
            },
          },
          {
            sourceId: 'suggestedLitters',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: ALGOLIA_LITTERS_INDEX!,
                    query,
                  },
                ],
              });
            },
            templates: {
              header() {
                return <p className="global-search-result-header text-dark-gray">Litters</p>;
              },
              item({ item, components }) {
                return (
                  <SuggestedHit
                    hit={item}
                    components={components}
                    attribute="global_hit_title"
                    route={item.route}
                  />
                );
              },
              footer() {
                return (
                  <div className="mt-3">
                    <a
                      className="text-dark-gray"
                      href={`${LITTERS_ADMIN_PREFIX}?query=${query}`}
                    >
                      See all Litters
                    </a>
                  </div>
                );
              },
            },
          },
          {
            sourceId: 'suggestedAccounts',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: ALGOLIA_USERS_INDEX!,
                    query,
                  },
                ],
              });
            },
            templates: {
              header() {
                return <p className="global-search-result-header text-dark-gray">Accounts</p>;
              },
              item({ item, components }) {
                return (
                  <SuggestedHit
                    hit={item}
                    components={components}
                    attribute="username"
                    route={`${USER_ADMIN_PREFIX}/${item.id}`}
                  />
                );
              },
              footer() {
                return (
                  <div className="mt-3">
                    <a
                      className="text-dark-gray"
                      href={`${USER_ADMIN_PREFIX}?query=${query}`}
                    >
                      See all Accounts
                    </a>
                  </div>
                );
              },
            },
          },
          {
            sourceId: 'suggestedProfiles',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: ALGOLIA_BREEDER_INDEX!,
                    query,
                  },
                ],
              });
            },
            templates: {
              header() {
                return <p className="global-search-result-header text-dark-gray">Profiles</p>;
              },
              item({ item, components }) {
                return (
                  <SuggestedHit
                    hit={item}
                    components={components}
                    attribute="name"
                    route={`${PROFILES_ADMIN_PREFIX}/${item.id}`}
                  />
                );
              },
              footer() {
                return (
                  <div className="mt-3">
                    <a
                      className="text-dark-gray"
                      href={`${USER_ADMIN_PREFIX}?type=sellers&query=${query}`}
                    >
                      See all Profiles
                    </a>
                  </div>
                );
              },
            },
          },
          {
            sourceId: 'suggestedContacts',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: ALGOLIA_SELLER_CONTACTS_INDEX!,
                    query,
                  },
                ],
              });
            },
            templates: {
              header() {
                return <p className="global-search-result-header text-dark-gray">Contacts</p>;
              },
              item({ item, components }) {
                return (
                  <SuggestedHit
                    hit={item}
                    components={components}
                    attribute="name"
                    route={`${PROFILES_ADMIN_PREFIX}/${item.id}`}
                  />
                );
              },
              footer() {
                return (
                  <div className="mt-3">
                    <a
                      className="text-dark-gray"
                      href={`${USER_ADMIN_PREFIX}?type=seller&query=${query}`}
                    >
                      Search all sellers
                    </a>
                  </div>
                );
              },
            },
          },
          {
            sourceId: 'suggestedOrders',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: ALGOLIA_ORDERS_INDEX!,
                    query,
                  },
                ],
              });
            },
            templates: {
              header() {
                return <p className="global-search-result-header text-dark-gray">Orders</p>;
              },
              item({ item, components }) {
                return (
                  <SuggestedHit
                    hit={item}
                    components={components}
                    attribute="global_hit_title"
                    route={`${ORDER_ADMIN_VIEW_PREFIX}/${item.id}`}
                  />
                );
              },
              footer() {
                return (
                  <div className="mt-3">
                    <a
                      className="text-dark-gray"
                      href={`${ORDER_ADMIN_PREFIX}?query=${query}`}
                    >
                      See all Orders
                    </a>
                  </div>
                );
              },
            },
          },
        ]);
      }}
    />
  );
};

export default AdminGlobalSearch;

if (document.getElementById('admin-global-autocomplete')) {
  const element = document.getElementById('admin-global-autocomplete');
  ReactDOM.render(
    <AdminGlobalSearch />,
    element,
  );
}
